import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  DropdownButton,
  Dropdown,
} from "react-bootstrap";
import BarChart from "../util/BarChart";
import DoughnutChart from "../util/Doughnut";
import axiosInstance from "../api/axiosConfig";
import Loading from "./Loading";
import Error from "./Error";
import DateRangePicker from "./DateRangePicker";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { formatDateOnly } from "../util/dateUtil";
import NoRecordFound from "./NoRecordFound";

const Dashboard = () => {
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const today = new Date();
  const [startDate, setStartDate] = useState(startOfMonth(today));
  const [endDate, setEndDate] = useState(endOfMonth(today));
  const [selectedOption, setSelectedOption] = useState("Month");

  const fetchData = useCallback(async (queryString = {}) => {
    setError(null);
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `/file_status?start_date=${queryString.start_date}&end_date=${queryString.end_date}`
      );
      setStatusData({
        ...response.data.data,
        latest_files: [...(response.data.data?.latest_files || [])].reverse(),
      });
      setLoading(false);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  }, []);

  const handleFilter = useCallback(() => {
    fetchData({
      start_date: formatDateOnly(startDate),
      end_date: formatDateOnly(endDate),
    });
  }, [endDate, fetchData, startDate]);

  useEffect(() => {
    handleFilter();
  }, [handleFilter]);

  // print
  const handlePrint = () => {
    window.print();
  };

  const handleOptionChange = (event) => {
    const option = event;
    setSelectedOption(option);
    const today = new Date();
    switch (option) {
      case "Today":
        setStartDate(today);
        setEndDate(today);
        break;
      case "Week":
        setStartDate(startOfWeek(today));
        setEndDate(endOfWeek(today));
        break;
      case "Month":
        setStartDate(startOfMonth(today));
        setEndDate(endOfMonth(today));
        break;
      default:
        setStartDate(null);
        setEndDate(null);
    }
  };

  if (loading) {
    return <Loading />;
  }


  return (
    <>
      <Container fluid className="printable-content">
        {/* Dashboard Summary Section */}
        <Row>
          <Col xs="auto">
            <Button onClick={handlePrint} className="mb-2 " variant="light">
              <i className="fas fa-print"></i>
            </Button>
          </Col>

          <Col xs="auto">
            <DropdownButton
              id="dropdown-status-button"
              title={`${selectedOption}`}
              onSelect={(e) => handleOptionChange(e)}
              variant="outline-primary"
              className="btn-light dropdown-custom-color"
            >
              <Dropdown.Item eventKey="Today">Today</Dropdown.Item>
              <Dropdown.Item eventKey="Week">Week</Dropdown.Item>
              <Dropdown.Item eventKey="Month">Month</Dropdown.Item>
            </DropdownButton>
          </Col>
          <Col xs="auto">
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          </Col>

          <Col xs="auto">
            <Button
              onClick={handleFilter}
              className="btn-outline-steel-blue btn-light me-2 dropdown-custom-color"
            >
              Apply Filter
            </Button>
          </Col>
        </Row>
        {error && <Error message={error.message} height="80vh" />}
        {Object.keys(statusData).length > 0 &&
        (statusData?.success_count > 0 || statusData?.fail_count > 0) ? (
          <>
            <Row className="mt-5 mb-5">
              <Col md={6} className="upload_status">
                <Card>
                  <Card.Body>
                    <Card.Title>Upload Status</Card.Title>
                    <BarChart
                      data={[statusData?.success_count, statusData?.fail_count,  statusData?.partial_success_count]}
                    />
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className="documents_upload">
                <Card style={{ height: "100%" }}>
                  <Card.Body>
                    <Card.Title># of Documents Uploaded</Card.Title>
                    <Row className="mb-3">
                      <Col md={4}>
                        <DoughnutChart
                          data={statusData?.file_type_counts?.pdf || 0}
                        />
                        <Card.Text>PDF Files</Card.Text>
                      </Col>
                      <Col md={4}>
                        <DoughnutChart
                          data={
                            (statusData?.file_type_counts?.doc || 0) +
                            (statusData?.file_type_counts?.docx || 0)
                          }
                        />
                        <Card.Text>DOC Files</Card.Text>
                      </Col>
                      <Col md={4}>
                        <DoughnutChart
                          data={
                            (statusData?.file_type_counts?.xlsx || 0) +
                            (statusData?.file_type_counts?.xls || 0)
                          }
                        />
                        <Card.Text>EXCEL Files</Card.Text>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col>
                <Card>
                  <Card.Body>
                    <Card.Title>Recent Documents</Card.Title>
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Document Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          statusData?.latest_files.map((item, index) => (
                          <tr key={index}>
                            <td>{item}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <NoRecordFound
            height="80vh"
            message="No Record Found.Please select a different date range."
          />
        )}
      </Container>
    </>
  );
};

export default Dashboard;
